import React, { useCallback, useMemo } from 'react';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import PropTypes from 'prop-types';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { Empty, Spin } from 'antd';
import classNames from 'classnames';
import IntlMessages from 'util/IntlMessages';
import styles from './styles.module.less';
import { ALIGN_CLASSES } from './constants';

const SORT_COLUMN_KEY = '__sort__';

/**
 * SortableTable component renders a responsive table with row reordering capability.
 *
 * @param {Object[]} dataSource - The data source for the table.
 * @param {Object[]} columns - The columns configuration for the table.
 * @param {Function} onMove - Callback when rows are reordered. Receives the new dataSource array.
 * @param {string} [rowKey='id'] - The key to identify each row uniquely.
 * @param {string} [className=''] - Additional class names for the table.
 * @param {boolean} [loading=false] - Loading state for the table.
 * @param {string|null} [type=null] - Type of the table for additional styling.
 * @param {number} [sortColumnWidth=50] - Width of the sort column.
 * @param {boolean} [showSortColumn=true] - Whether to show the sort column.
 *
 * @returns {JSX.Element} The rendered sortable table component.
 */
const SortableTable = ({
  dataSource = [],
  columns = [],
  onMove,
  rowKey = 'id',
  className = '',
  loading = false,
  type = null,
  maxHeight = 'none',
  onScroll,
  sortColumnWidth = 50,
  showSortColumn = true,
  ...rest
}) => {
  const typeAux = {
    primary: styles.tablePrimary,
  };

  const getRowKey = useCallback((record, index) => {
    if (typeof rowKey === 'function') {
      return rowKey(record, index);
    }
    return record[rowKey] ?? index;
  }, [rowKey]);

  const moveRow = useCallback((fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= dataSource.length || !onMove) return;
    const newData = [...dataSource];
    const [removed] = newData.splice(fromIndex, 1);
    newData.splice(toIndex, 0, removed);
    onMove(newData);
  }, [dataSource, onMove]);

  const SortArrows = useCallback(({ index }) => {
    const isFirst = index === 0;
    const isLast = index === dataSource.length - 1;

    return (
      <div className={styles.sortArrows}>
        <span onClick={() => moveRow(index, index - 1)} className={classNames(styles.sortArrow, { [styles.sortArrowDisabled]: isFirst }, 'material-symbols-outlined')}>
          keyboard_arrow_up
        </span>
        <span onClick={() => moveRow(index, index + 1)} className={classNames(styles.sortArrow, { [styles.sortArrowDisabled]: isLast }, 'material-symbols-outlined')}>
          keyboard_arrow_down
        </span>
      </div>
    );
  }, [dataSource.length, moveRow]);

  const sortColumn = useMemo(() => ({
    key: SORT_COLUMN_KEY,
    title: null,
    dataIndex: SORT_COLUMN_KEY,
    width: sortColumnWidth,
    align: 'center',
    contentAlign: 'center',
  }), [sortColumnWidth]);

  const mergedColumns = useMemo(() => {
    if (!showSortColumn) return columns;
    return [sortColumn, ...columns];
  }, [columns, showSortColumn, sortColumn]);

  const renderCellContent = (column, record, index) => {
    if (column.key === SORT_COLUMN_KEY) {
      return <SortArrows index={index} />;
    }
    if (column.render) {
      return column.render(record[column.dataIndex], record, index);
    }
    return record[column.dataIndex];
  };

  return (
    <Spin spinning={loading}>
      <div
        className={styles.tableWrapper}
        style={{ maxHeight }}
        onScroll={onScroll}
        {...rest}
      >
        <Table className={classNames(styles.tableContainer, type && typeAux[type], className)}>
          <Thead>
            <Tr>
              {mergedColumns.map(column => (
                <Th
                  style={{ width: column?.width, ...column?.styles }}
                  className={classNames(ALIGN_CLASSES[column?.align || 'left'])}
                  key={column.key}
                >
                  {column?.title}
                </Th>
              ))}
            </Tr>
          </Thead>
          {dataSource.length > 0 && !loading ? (
            <Tbody>
              {dataSource.map((record, index) => (
                <Tr key={getRowKey(record, index)}>
                  {mergedColumns.map(column => (
                    <Td
                      className={classNames(ALIGN_CLASSES[column?.contentAlign])}
                      key={column.key}
                    >
                      {renderCellContent(column, record, index)}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          ) : (
            <Tbody>
              <tr>
                <td colSpan={mergedColumns.length}>
                  <Empty description={<IntlMessages id="general.empty.state" />} />
                </td>
              </tr>
            </Tbody>
          )}
        </Table>
      </div>
    </Spin>
  );
};

SortableTable.propTypes = {
  dataSource: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      dataIndex: PropTypes.string.isRequired,
      render: PropTypes.func,
      width: PropTypes.any,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      contentAlign: PropTypes.oneOf(['left', 'center', 'right']),
      styles: PropTypes.object,
    }),
  ).isRequired,
  onMove: PropTypes.func.isRequired,
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  className: PropTypes.string,
  loading: PropTypes.bool,
  type: PropTypes.string,
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onScroll: PropTypes.func,
  sortColumnWidth: PropTypes.number,
  showSortColumn: PropTypes.bool,
};

export default React.memo(SortableTable);
